import { useState, useEffect, useCallback } from 'react';
import sessionsService, { UserSession } from '@/services/sessionsService';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook pour gérer automatiquement les sessions utilisateur
 */
export const useSessionManager = () => {
  const { isAuthenticated, user } = useAuth();
  const [sessionInitialized, setSessionInitialized] = useState(false);

  /**
   * Initialise la session lors du montage du composant
   */
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setSessionInitialized(false);
      return;
    }

    const initializeSession = async () => {
      try {
        await sessionsService.initializeSession();
        setSessionInitialized(true);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de la session:', error);
        setSessionInitialized(false);
      }
    };

    initializeSession();
  }, [isAuthenticated, user]);

  /**
   * Met à jour l'activité lors des interactions utilisateur
   */
  const updateActivity = useCallback(async () => {
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      await sessionsService.updateActivityOnInteraction();
    } catch (error: any) {
      // Ne pas logger les erreurs 401 (non authentifié) pour éviter le spam
      if (error?.response?.status !== 401) {
        console.error('Erreur lors de la mise à jour de l\'activité:', error);
      }
    }
  }, [isAuthenticated, user]);

  /**
   * Ajoute des listeners pour détecter les interactions utilisateur
   */
  useEffect(() => {
    if (!isAuthenticated || !user || !sessionInitialized) {
      return;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    let timeoutId: NodeJS.Timeout | null = null;
    
    const handleUserActivity = () => {
      // Délai de 2 secondes pour éviter les appels trop fréquents
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        updateActivity();
      }, 2000);
    };

    // Ajouter les listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Nettoyer les listeners
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [updateActivity, isAuthenticated, user, sessionInitialized]);

  return {
    updateActivity
  };
};

/**
 * Hook pour gérer les sessions actives avec état
 */
export const useActiveSessions = () => {
  const { isAuthenticated } = useAuth();
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Charge les sessions actives
   */
  const loadSessions = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await sessionsService.getActiveSessions();
      setSessions(response.sessions);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Termine une session spécifique
   */
  const terminateSession = useCallback(async (sessionId: string) => {
    try {
      await sessionsService.terminateSession(sessionId);
      await loadSessions(); // Recharger la liste
    } catch (err: any) {
      setError(err.message);
    }
  }, [loadSessions]);

  /**
   * Termine toutes les autres sessions
   */
  const terminateAllOtherSessions = useCallback(async () => {
    try {
      await sessionsService.terminateAllOtherSessions();
      await loadSessions(); // Recharger la liste
    } catch (err: any) {
      setError(err.message);
    }
  }, [loadSessions]);

  /**
   * Nettoie les sessions expirées
   */
  const cleanupExpiredSessions = useCallback(async () => {
    try {
      await sessionsService.cleanupExpiredSessions();
      await loadSessions(); // Recharger la liste
    } catch (err: any) {
      setError(err.message);
    }
  }, [loadSessions]);

  // Charger les sessions au montage
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  return {
    sessions,
    loading,
    error,
    loadSessions,
    terminateSession,
    terminateAllOtherSessions,
    cleanupExpiredSessions
  };
};
